import React, { useLayoutEffect, useRef } from 'react';
import {
  FeatureBlurbs,
  HeroSection,
  MessagingInvitationsSection,
  OrganizingGuests,
  StopGuestimating,
  TagSection,
  EditingGuestlist,
  TagTheyAreIt,
  EmotionalBreakSection,
  OverviewArt,
  EmailInviteTrackingSection,
} from '@sections/guest-list';
import { AbcSection } from '@sections/shared/AbcSection';
import { ColoredBlockSection } from '@sections/shared/ColoredBlockSection';
import { SpeechBubblesGuestList } from '@sections/shared/SpeechBubblesGuestList';
import { RSVPTrackingSection } from '@sections/online-rsvp/RSVPTrackingSection';
import { FaqList } from '@components/FaqList';
import { MoreTimeTogether } from '@sections/guest-list/MoreTimeTogether';
import { WeddingWebsite } from '@components/WeddingWebsite';
import { Layout, Seo } from '@layout';
import { guestListData } from '@data-ts/pages/guest-list';
import { useIsMounted } from '@hooks/useIsMounted';
import { useTheme } from '@styledComponents';
import { topHeaderLinkHeroTransitionClassName } from 'src/layout/components/Header/Header.styles';
import { gsap } from 'gsap';
import { faqQuestionsData } from '@data-ts/components/FaqQuestions';
import { weddingWebsiteData } from '@data-ts/components/WeddingWebsite';
import { testimonialsData } from '@data-ts/components/Testimonials';
import TestimonialSection from '@sections/shared/TestimonialSection/TestimonialSection';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';

const PAGE: MarcomPageName = 'guest-list';

const GuestList = () => {
  const headerRef = useRef<HTMLElement>(null);
  const moreTimeTogetherSectionId = 'Bucket_Listing';
  const theme = useTheme();
  const isMounted = useIsMounted();
  const isMobileDevice = useThemeMediaQuery('below_sm');
  const { speechBubbles } = guestListData || {};
  useLayoutEffect(() => {
    let newHeaderTimeline: ReturnType<typeof gsap.timeline>;
    if (isMounted && headerRef.current) {
      newHeaderTimeline = gsap.timeline({ paused: true });
      const textBlockColorTo = theme.colors.linkDefault;
      newHeaderTimeline.to(
        headerRef.current.querySelectorAll(`a.${topHeaderLinkHeroTransitionClassName}`),
        {
          color: textBlockColorTo,
        },
        0
      );
    }
  }, [isMounted, theme]);
  return (
    <Layout
      headerRef={headerRef}
      headerScrollThresholdVh={10}
      isTransparentHeader={true}
      transparentJoyLogoColor="purple6"
      transparentHeaderButtonVariant="primary"
      transparentHeaderLinkVariant="navLink"
      page={PAGE}
    >
      <Seo
        title={guestListData.seo.title}
        description={guestListData.seo.content}
        canonicalURL={'https://withjoy.com/guest-list/'}
        ogImage={'common/meta_share/guest-list.jpg'}
      />
      <HeroSection />
      <OverviewArt />
      <div id={moreTimeTogetherSectionId}>
        <MoreTimeTogether />
      </div>
      <EditingGuestlist backgroundColor="white" />
      <TagTheyAreIt />
      <FeatureBlurbs backgroundColor="white" />
      <TagSection tags={guestListData.tagNames} />
      <OrganizingGuests backgroundColor="white" />
      <EmotionalBreakSection />
      <MessagingInvitationsSection />
      <SpeechBubblesGuestList
        backgroundColor="white"
        variant="humana"
        eyebrow={speechBubbles.eyebrow}
        title={speechBubbles.title}
        body={speechBubbles.content}
        imagesSrc={
          isMobileDevice
            ? 'guest-list/email-tracking-and-reminders/mobile-message.jpg'
            : 'guest-list/email-tracking-and-reminders/general-message.jpg'
        }
        isHorizontal={true}
      />
      <EmailInviteTrackingSection />
      <StopGuestimating backgroundColor="white" />
      <RSVPTrackingSection page="guestList" backgroundColor="white" />
      <WeddingWebsite
        frameVariant="goldenhaze"
        colorVariant="yellow"
        text={weddingWebsiteData.guestList}
        pageVariant="guest-list"
        btnVariant="secondary"
        linkVariant="primary"
      />
      <ColoredBlockSection
        backgroundColor="white"
        sideHeaderText={guestListData.rsvpText}
        sectionId="Guest_List_FAQ"
        isToggleNeeded={true}
        bottonBottom={isMobileDevice ? '0px' : '120px'}
      >
        <FaqList
          accordion={true}
          questionNodes={[...faqQuestionsData.guestList, ...faqQuestionsData.general]}
          color="neutra6"
          bodyColor={'neutra5'}
        />
      </ColoredBlockSection>
      <TestimonialSection carouselVariant="left" backgroundColor="purple5" quotes={testimonialsData.guestList} mobileQuoteIndex={2} />
      <AbcSection abcSectionId="Guest_List_ABC_CTA" page={PAGE} />
    </Layout>
  );
};

export default GuestList;
